import React, { useState, useEffect } from "react";
import { Paper, Typography, Grid2, Divider } from "@mui/material";
import { useForm } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';
import dayjs from "dayjs";

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import ControlledTextField from "@components/inputs/controlledTextField";
import ControlledAutocomplete from "@components/inputs/controlledAutocomplete";
import ControlledRichTextEditor from "@components/inputs/controlledRichTextEditor";
import ControlledDatePicker from "@components/inputs/controlledDatePicker";

import { useSnackbar } from "@context/snackbarContext";
import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";
import { formatDateTime } from "@utils/formatter";


const schema = z.object({
  fundName: z.string().min(1, 'Fund Name is required'),
  description: z.string().min(1, { message: 'Description is required' }),

  titleEng: z.string().min(1, 'Title is required'),
  titleCnsimp: z.string().min(1, '标题 is required'),
  titleCntrad: z.string().min(1, '標題 is required'),

  contentEng: z.string().min(1, 'Content is required'),
  contentCnsimp: z.string().min(1, '内容 is required'),
  contentCntrad: z.string().min(1, '內容 is required'),

  isLongTerm: z.string().min(1, 'This field is required'),

  fundType: z.object({
    fundType: z.string().min(1, "Label is required"),
    fundTypeGuid: z.string().min(1, "Value is required"),
  }, {invalid_type_error: 'Fund Type is required'}),

  startDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({ invalid_type_error: 'Start Date is required' }))
    .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }).optional().nullable(),

  endDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({ invalid_type_error: 'End Date is required' }))
    .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }).optional().nullable(),

  bank: z.array(z.object({
    value: z.string().min(1, 'Value is required'),
    label: z.string().min(1, 'Label is required'),
  })).min(1, 'At least one bank is required'),

  /* view only */
  createdOn: z.string().optional(),
  createdBy: z.string().optional(),
})
.superRefine((data, ctx) => {
  if (data.isLongTerm === "No") {
    if (!data.startDate) {
      ctx.addIssue({
        path: ["startDate"],
        message: "Start Date is required when Long Term is No",
        code: "custom",
      });
    }
    if (!data.endDate) {
      ctx.addIssue({
        path: ["endDate"],
        message: "End Date is required when Long Term is No",
        code: "custom",
      });
    }
    if (data.startDate && data.endDate && data.endDate < data.startDate) {
      ctx.addIssue({
        path: ["endDate"],
        message: "End Date cannot be earlier than Start Date",
        code: "custom",
      });
    }
  }
});

export default function FundForm() {

  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const navigate = useNavigate()
  const { pathname } = useLocation();
  const { user } = useAuthentication();
  const pageAction = pathname.split("/")[3] || null; // get from pathname expected values ["create" || "view" || "edit"]
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const { handleSubmit, control, getValues, reset, watch } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      isLongTerm: "Yes",
      bank: [],
    }
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  useEffect(()=>{
    if (((pageAction === "edit" || pageAction === "view") && !id) || (pageAction === "create" && id)) {
      onBack()
    }
    if (id) {
      fetchFundMutation.mutate(id)
    }
  },[pageAction])

  const bankOptions = useQuery({
    queryKey: ['getBankList'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getBankList),
    initialData: [],
    select: (res) => {
      const data = res?.data?.filter(item => item.isDonation === true) || [];

      return data.map(item => ({
        value: item.bankGuid,
        label: `${item.bankName} - ${item.accountNumber}`
      }));
    },
  })

  const fetchFundMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${AdminApiEndpoints.getFund}/${id}`)
    },
    onSuccess: (res) => {
      const {createdOn, fundBanks, ...data} = res.data
      const createdOnFormatted = formatDateTime(createdOn)
      const isLongTerm = data?.isLongTerm ? "Yes" : "No"
      const fundType = {fundType: data?.fundType?.valueEng, fundTypeGuid: data?.fundType?.parameterGuid}
      const bank = fundBanks.map(item => ({
        value: item.bank.bankGuid,
        label: `${item.bank.bankName} - ${item.bank.accountNumber}`
      }));
      reset({...data, createdOn: createdOnFormatted, isLongTerm, fundType, bank})
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve fund", "error");
    }
  });

  const fundTypeOptions = useQuery({
    queryKey: ['getFundTypeList'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getFundTypeList),
    initialData: [],
    select: (res) => {
      const data = res?.data || [];
      return data.map(item => ({
        fundTypeGuid: item.parameterGuid,
        fundType: item.valueEng
      }));
    },
  })

  const createFundMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(AdminApiEndpoints.createFund, {...data, by: user.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getFund'] })
      openSnackbar("Successfully created fund", "success");
      onBack()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to create fund", "error");
    }
  })

  const updateFundMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.updateFund}/${id}`, {...data, by: user.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getFund'] })
      openSnackbar("Successfully updated fund", "success");
      fetchFundMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to update fund", "error");
    }
  })

  const onSubmit = () => {
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    const {startDate, endDate, fundType, isLongTerm, bank, ...data} = getValues();
    const isLongTermFormatted = isLongTerm === "Yes" ? true : false
    const startDateFormatted = !isLongTermFormatted ? dayjs(startDate).format("YYYY-MM-DD") : null
    const endDateFormatted = !isLongTermFormatted ? dayjs(endDate).format("YYYY-MM-DD") : null
    data.fundTypeGuid = fundType?.fundTypeGuid
    data.bankGuid = bank.map(item => (item.value))
    // console.log({...data, isLongTerm: isLongTermFormatted, startDate: startDateFormatted, endDate: endDateFormatted, by: user.username})
    // return
    
    if(pageAction === "create")
      createFundMutation.mutate({...data, isLongTerm: isLongTermFormatted, startDate: startDateFormatted, endDate: endDateFormatted})
    else
      updateFundMutation.mutate({...data, isLongTerm: isLongTermFormatted, startDate: startDateFormatted, endDate: endDateFormatted})
  }

  const onBack = () => {
    navigate('/admin/fund-setup', { replace: true });
  }

  // console.log(watch('content'))
  const watchIsLongTerm = watch('isLongTerm')

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs />
      <SkeletonWrapper isLoading={fetchFundMutation.isPending} isError={fetchFundMutation.isError} errorMessage="Unable to load fund" disabled={["create"].includes(pageAction)}>
      <form onSubmit={handleSubmit(onSubmit)} style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Fund Detail</Typography>
            </Grid2>
            <Grid2 size={12}>
              <ControlledTextField 
                name="fundName"
                control={control}
                label="Fund Name"
                disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={12}>
              <ControlledTextField 
                name="description"
                control={control}
                label="Description"
                disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                readOnly={["view"].includes(pageAction)}
                multiline
                rows={2}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledAutocomplete 
                name="fundType"
                control={control}
                label="Fund Type"
                disabled={fetchFundMutation.isPending || fetchFundMutation.isError || ["view"].includes(pageAction)}
                options={fundTypeOptions.data}
                loading={fundTypeOptions.isFetching}
                getOptionLabel={(option) => option.fundType}
                getOptionKey={(option) => option.fundTypeGuid}
                isOptionEqualToValue={(option, value) => option.fundTypeGuid === value?.fundTypeGuid}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledAutocomplete 
                name="isLongTerm"
                control={control}
                label="Long Term?"
                disabled={fetchFundMutation.isPending || fetchFundMutation.isError || ["view"].includes(pageAction)}
                options={["Yes", "No"]}
                disableClearable
              />
            </Grid2>

            {watchIsLongTerm === "No" && <>
            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledDatePicker 
                name="startDate"
                control={control}
                label="Start Date"
                disabled={fetchFundMutation.isPending || fetchFundMutation.isError || ["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledDatePicker 
                name="endDate"
                control={control}
                label="End Date"
                disabled={fetchFundMutation.isPending || fetchFundMutation.isError || ["view"].includes(pageAction)}
              />
            </Grid2>
            </>}

            <Grid2 size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
              <ControlledAutocomplete 
                name="bank"
                control={control}
                label="Bank Account"
                disabled={fetchFundMutation.isPending || fetchFundMutation.isError || ["view"].includes(pageAction)}
                options={bankOptions.data}
                loading={bankOptions.isFetching}
                multiple
                disableClearable
                isOptionEqualToValue={(option, value) => option.value === value.value}
              />
            </Grid2>

            {["view", "edit"].includes(pageAction) && <>

            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdOn"
                control={control}
                label="Created On"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdBy"
                control={control}
                label="Created By"
                disabled={true}
              />
            </Grid2>

            </>}

          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>捐款内容 (简体)</Typography>
            </Grid2>
            <Grid2 size={{lg: 6, md: 6, xs: 12}}>
              <ControlledTextField 
                name="titleCnsimp"
                control={control}
                label="标题"
                disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
            <Grid2 size={12}>
              <ControlledRichTextEditor 
                name="contentCnsimp"
                control={control}
                disabled={["view"].includes(pageAction)}
              />
            </Grid2>
          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>捐款內容 (繁体)</Typography>
            </Grid2>
            <Grid2 size={{lg: 6, md: 6, xs: 12}}>
              <ControlledTextField 
                name="titleCntrad"
                control={control}
                label="標題"
                disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
            <Grid2 size={12}>
              <ControlledRichTextEditor 
                name="contentCntrad"
                control={control}
                disabled={["view"].includes(pageAction)}
              />
            </Grid2>
          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Fund Content</Typography>
            </Grid2>
            <Grid2 size={{lg: 6, md: 6, xs: 12}}>
              <ControlledTextField 
                name="titleEng"
                control={control}
                label="Title"
                disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
            <Grid2 size={12}>
              <ControlledRichTextEditor 
                name="contentEng"
                control={control}
                disabled={["view"].includes(pageAction)}
              />
            </Grid2>
          </Grid2>
        </Paper>

        {["create", "edit"].includes(pageAction) && 
          <Paper
            sx={{
              p: "20px 25px",
              boxShadow: "none",
              borderRadius: "10px",
              border: "1px solid #E5E5E5",
              overflowX: "auto",
              display: "flex",
              justifyContent: 'flex-end',
              gap: "10px"
            }}
          >
            <CustomButton type="submit" disabled={createFundMutation.isPending || fetchFundMutation.isError}>Submit</CustomButton>
          </Paper>
        }
      </form>
      </SkeletonWrapper>
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription="Submit fund?"
        onSubmit={()=>onConfirmSubmit()}
        loading={createFundMutation.isPending || updateFundMutation.isPending}
      />
    </div>
  );
}
